import Button from 'ext-ui/components/Button/Button'
import React, { ReactElement } from 'react'
import { useTranslate } from 'src/localization/Trns'

import type { CardDetailsAvailability } from '../cardDetailHeroMapping'
import SvgEye from '../icons/SvgEye'
import SvgEyeOff from '../icons/SvgEyeOff'
import SvgSnowflake from '../icons/SvgSnowflake'
import * as style from './CardDisplayActions.style'

type CardDisplayActionsProps = {
  /** Exact capability-gated action that the button will execute. */
  freezeAction: 'freeze' | 'unfreeze' | undefined
  /** Runs the capability-gated freeze or unfreeze mutation. */
  onFreeze: () => void
  canFreeze: boolean
  freezePending: boolean
  /** Reveal-disclosure state, owned by the hero shell. */
  revealOpen: boolean
  onToggleReveal: () => void
  /**
   * EX-40596 source selector, shared with the Available credit region. False
   * (the default) means the reveal resolves from demo constants, so it is always
   * offered and never gated. True means the live path, where the capability can
   * deny, be pending, or be silent.
   */
  cardHeaderLiveDataEnabled: boolean
  cardDetailsAvailability: CardDetailsAvailability | undefined
  cardDetailsCapabilityError: boolean
  /**
   * The reveal panel's DOM id, generated with `useId` by whichever variant owns
   * this cluster (the shell for `full`, the compact bar for `compact`) and shared
   * with its `CardRevealPanel`, so the Show details trigger's `aria-controls`
   * resolves to the right panel while both heroes are mounted.
   */
  revealPanelId: string
}

/**
 * The card-display action cluster in the hero toolbar: Show details (the reveal
 * disclosure trigger) then Freeze. The `•••` menu joins in slice 3.
 *
 * The freeze label is derived from the lifecycle controller's exact action so
 * copy and behavior cannot diverge while the display-status query refreshes.
 */
const CardDisplayActions: React.FC<CardDisplayActionsProps> = ({
  freezeAction,
  onFreeze,
  canFreeze,
  freezePending,
  revealOpen,
  onToggleReveal,
  cardHeaderLiveDataEnabled,
  cardDetailsAvailability,
  cardDetailsCapabilityError,
  revealPanelId,
}): ReactElement => {
  const translate = useTranslate()

  const freezeLabel =
    freezeAction === 'unfreeze' ? translate('Unfreeze') : translate('Freeze')

  // The demo path issues no request and consults no capability, so neither a
  // verdict nor a transport failure describes it — it reads as an outright
  // grant. Resolving that here once keeps the three states below free of the
  // flag: they then say only what each one means.
  const availability = cardHeaderLiveDataEnabled
    ? cardDetailsAvailability
    : 'AVAILABLE'
  const capabilityError =
    cardHeaderLiveDataEnabled && cardDetailsCapabilityError

  // READ_ONLY is a grant: revealing details is itself a read-only operation.
  const canReveal = availability === 'AVAILABLE' || availability === 'READ_ONLY'
  const capabilityPending = availability === undefined
  // Everything that is neither a grant nor still pending is a refusal — today
  // UNAVAILABLE and UNKNOWN. UNKNOWN sits with UNAVAILABLE rather than on its
  // own because on the live path the server has named no verdict, and to the
  // user that is indistinguishable from a denial; rendering nothing instead
  // would make enabling the flag look like the reveal had been taken away.
  const capabilityUnavailable =
    capabilityError || (!canReveal && !capabilityPending)

  // `disabled` would remove the button from the tab order, so neither `aria-busy`
  // nor the loading label could ever be reached by a keyboard or screen-reader
  // user — the state would exist without being conveyed, which `src/CLAUDE.md`'s
  // WCAG 2.1 AA requirement does not allow. `aria-disabled` keeps it focusable
  // and readable; the click guard below keeps it un-activatable.
  const busy = !revealOpen && capabilityPending

  return (
    <div css={style.actions}>
      {revealOpen || !capabilityUnavailable ? (
        <Button
          variant="secondary"
          iconLeft={
            revealOpen ? (
              <SvgEyeOff width={15} height={15} />
            ) : (
              <SvgEye width={15} height={15} />
            )
          }
          css={style.ariaDisabled}
          onClick={() => {
            if (!busy) onToggleReveal()
          }}
          aria-disabled={busy}
          aria-busy={busy}
          aria-expanded={revealOpen}
          aria-controls={revealOpen ? revealPanelId : undefined}
          data-testid="card-show-details-button"
        >
          {revealOpen
            ? translate('Hide details')
            : capabilityPending
              ? translate('Loading secure card details')
              : translate('Show details')}
        </Button>
      ) : (
        <span css={style.unavailableText} role="status">
          {translate('Card details are unavailable for this card')}
        </span>
      )}
      <Button
        variant="secondary"
        iconLeft={<SvgSnowflake width={15} height={15} />}
        onClick={onFreeze}
        disabled={!canFreeze || freezePending}
        loading={freezePending}
        aria-busy={freezePending}
        data-testid="card-freeze-button"
      >
        {freezeLabel}
      </Button>
    </div>
  )
}

export default CardDisplayActions
